import React from "react";
import {
  BookOpen, CreditCard, Target, PhoneCall, CalendarCheck2, CheckCircle2,
  ArrowRight, ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const STEPS = [
  {
    n: 1,
    icon: CreditCard,
    tint: "text-teal-500 bg-teal-500/15",
    title: "Enter your data",
    where: "Debts · Bills · Cash Flow",
    to: "/debts",
    cta: "Go to Debts",
    body: "This is the only setup that takes real time — and you only do it once.",
    how: [
      "Open Debts → Add debt and enter each credit card and loan: balance, APR, minimum payment and (for cards) credit limit.",
      "Each debt automatically gets a linked monthly bill, so you don't re-enter it on the Bills page.",
      "On Bills, add your other recurring bills (rent, utilities, subscriptions) with their due dates.",
      "On Cash Flow, add your paychecks so OWEverit can see income vs. bills vs. planned payments.",
    ],
  },
  {
    n: 2,
    icon: Target,
    tint: "text-indigo-500 bg-indigo-500/15",
    title: "Find your payment plan",
    where: "Debts · Simulator",
    to: "/debts",
    cta: "Open your plan",
    body: "Let OWEverit decide where every extra dollar does the most damage.",
    how: [
      "On Debts, open \"Optimize your payoff\" and pick a strategy: Snowball (smallest balance first), Avalanche (highest APR first) or Cash Flow Index (frees the most monthly cash first).",
      "Set your monthly extra — the amount beyond all your minimums you can commit to debt.",
      "Read your debt-free date and the interest you'll avoid. It updates instantly.",
      "Want to compare scenarios first? Use the Simulator to test different strategies and extra amounts side by side.",
    ],
  },
  {
    n: 3,
    icon: PhoneCall,
    tint: "text-amber-500 bg-amber-500/15",
    title: "Lower your rates (optional)",
    where: "Debts · Simulator",
    to: "/debts",
    cta: "See rate tools",
    body: "A lower APR sends more of every payment to principal — sometimes a 10-minute call beats months of extra payments.",
    how: [
      "On Debts, open the APR-reduction script for your highest-rate card and call your issuer to ask for a lower rate.",
      "Comparing a balance-transfer offer? Use the Simulator to weigh the new rate against transfer fees before you commit.",
      "Re-check your plan afterward — a lower rate pulls your debt-free date closer automatically.",
    ],
  },
  {
    n: 4,
    icon: CalendarCheck2,
    tint: "text-emerald-500 bg-emerald-500/15",
    title: "Set up automatic payments with your lender",
    where: "Each lender / card website",
    to: null,
    cta: null,
    body: "OWEverit never links to or moves money from your accounts — you set autopay directly with each lender, and OWEverit tells you exactly what to schedule.",
    how: [
      "Log in to each lender or card website and turn on autopay for at least the minimum, timed to arrive by the due date — this protects you from late fees.",
      "For your current target debt, schedule the extra amount from your plan on top of its minimum.",
      "Check your OWEverit calendar for the due dates and the exact amount to pay on each account.",
      "Because nothing is linked to your bank, your numbers stay yours — there's nothing to leak.",
    ],
  },
  {
    n: 5,
    icon: CheckCircle2,
    tint: "text-rose-500 bg-rose-500/15",
    title: "Track it each month",
    where: "Dashboard · Debts",
    to: "/dashboard",
    cta: "Open Dashboard",
    body: "Consistency beats perfection — check in once a month and let the plan compound.",
    how: [
      "Log each payment on the Debts page so balances, interest-saved and your streak stay accurate.",
      "When a debt is paid off, its freed-up minimum rolls into your next target automatically — no re-planning needed.",
      "Follow the \"Do This Next\" card on your Dashboard and re-run the Simulator whenever life changes (a raise, a windfall, a new card).",
    ],
  },
];

const FAQ = [
  { q: "What's the difference between Snowball and Avalanche?", a: "Both pay minimums on everything, then throw all your extra cash at ONE debt. Snowball targets the smallest balance first for quick, motivating wins. Avalanche targets the highest APR first, which mathematically saves the most interest. OWEverit shows you both so you can pick momentum or maximum savings." },
  { q: "What is the Cash Flow Index (CFI) strategy?", a: "CFI = balance ÷ minimum payment. It targets the debt with the LOWEST index first, because that debt eats the most monthly cash flow relative to its size — so paying it off frees up the most room in your budget the fastest. Rule of thumb: under ~50 is efficient to pay off, 50–100 is caution, and over 100 barely affects your monthly cash flow. Pick CFI when monthly breathing room matters more to you than total interest saved." },
  { q: "What is a 'snowball amount'?", a: "It's the extra money — beyond all your minimum payments — that you commit to debt each month. OWEverit puts it on your target debt, and when a debt is paid off its freed-up minimum rolls into the snowball automatically, so you pay off each next debt faster." },
  { q: "Why does credit utilization matter if I'm paying things off anyway?", a: "Utilization (balance ÷ limit) is one of the biggest factors in your credit score. Keeping it under 30% (ideally under 10%) can raise your score, which helps you qualify for lower rates and balance-transfer offers that make payoff cheaper. Paying a card down before its statement date lowers the number reported to bureaus." },
  { q: "What's the best way to use this app?", a: "Add everything once, pick a plan, then check in each month: log your payments, follow the 'Do This Next' card on your dashboard, and use the simulator whenever your situation changes (a raise, a windfall, a new card). Consistency beats perfection — the streak and reminders are there to keep you going." },
  { q: "Should I pay minimums or the full balance?", a: "'Minimum due this month' is the floor that keeps accounts current. 'Total owed (bills)' is the full amount still unpaid. To actually get out of debt you pay the minimums PLUS your snowball on the target debt — that's what shrinks balances and interest." },
  { q: "How does a balance transfer or APR reduction help?", a: "Lowering your interest rate means more of every payment goes to principal instead of the lender. Use the Simulator's balance-transfer lab to compare offers (including fees), and the APR-negotiation script on the Debts page to ask your issuer for a lower rate." },
  { q: "Does OWEverit connect to my bank?", a: "No. OWEverit never links to your bank or moves money. You enter your balances yourself and set autopay directly with each lender — so there's no linked account to leak. You can export or delete your data anytime from Settings." },
  { q: "Is my data shared with anyone?", a: "No. Nothing is shared until you explicitly invite a partner to a household. You control your data and can export or delete it anytime from Settings." },
];

export default function Guide() {
  return (
    <div className="space-y-8" data-testid="guide-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> Guide & FAQ</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">How to get to $0 with OWEverit</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Five steps, start to finish. Set it up once, pick your plan, put payments on autopay with your lenders, and check in each month.
        </p>
      </header>

      {/* How-to steps */}
      <section className="space-y-4">
        {STEPS.map((s) => (
          <div key={s.n} className="p-6 rounded-2xl border border-border bg-card" data-testid={`guide-step-${s.n}`}>
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${s.tint}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-muted-foreground">Step {s.n}</span>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-accent text-muted-foreground">{s.where}</span>
                </div>
                <h2 className="font-display text-xl font-bold mt-1">{s.title}</h2>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.body}</p>

                <ol className="mt-4 space-y-2.5">
                  {s.how.map((h, i) => (
                    <li key={`${s.n}-${i}`} className="flex gap-3 text-sm leading-relaxed" data-testid={`guide-step-${s.n}-how-${i}`}>
                      <span className="h-5 w-5 rounded-full bg-accent text-foreground/70 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ol>

                {s.to && (
                  <Link to={s.to} data-testid={`guide-step-${s.n}-cta`}
                    className="inline-flex items-center gap-1.5 mt-4 px-3.5 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-accent transition">
                    {s.cta} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Privacy reassurance */}
      <section className="p-5 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 flex items-start gap-3" data-testid="guide-privacy">
        <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
        <p className="text-sm leading-relaxed">
          <span className="font-semibold">No bank linking, ever.</span> You enter your own numbers and schedule autopay directly with each lender.
          OWEverit tells you what to pay and when — it never touches your accounts, so your data stays yours.
        </p>
      </section>

      {/* FAQ */}
      <section className="space-y-4">
        <h2 className="font-display text-xl font-bold">Frequently asked questions</h2>
        <div className="p-2 sm:p-4 rounded-2xl border border-border bg-card">
          <Accordion type="single" collapsible data-testid="guide-faq">
            {FAQ.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="last:border-b-0">
                <AccordionTrigger className="text-left font-semibold">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
